import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  orderBy
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Catalog, CatalogItem, Category, DietaryTag, AddOn, ItemVariation } from '../models';
import { Table } from '../models/Order';

/**
 * Fetch a catalog by ID
 */
export const getCatalogById = async (catalogId: string): Promise<Catalog | null> => {
  try {
    const catalogRef = doc(db, 'catalogs', catalogId);
    const catalogSnap = await getDoc(catalogRef);

    if (catalogSnap.exists()) {
      return { id: catalogSnap.id, ...catalogSnap.data() } as Catalog;
    }
    return null;
  } catch (error) {
    console.error('Error fetching catalog:', error);
    throw error;
  }
};

/**
 * Fetch all items for a catalog
 */
export const getCatalogItems = async (catalogId: string): Promise<CatalogItem[]> => {
  try {
    const itemsRef = collection(db, 'catalogs', catalogId, 'items');
    const q = query(itemsRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);

    const items = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as CatalogItem[];
    // Sort by order field (lower numbers first), then preserve createdAt order
    return items.sort((a, b) => {
      if (a.order !== undefined && b.order !== undefined) return a.order - b.order;
      if (a.order !== undefined) return -1;
      if (b.order !== undefined) return 1;
      return 0;
    });
  } catch (error) {
    console.error('Error fetching catalog items:', error);
    throw error;
  }
};

/**
 * Fetch visible items only (not hidden and in stock)
 */
export const getVisibleCatalogItems = async (catalogId: string): Promise<CatalogItem[]> => {
  try {
    const items = await getCatalogItems(catalogId);
    return items.filter(item =>
      !item.hiddenFromCustomers && !item.outOfStock
    );
  } catch (error) {
    console.error('Error fetching visible items:', error);
    throw error;
  }
};

/**
 * Fetch a single catalog item
 */
export const getCatalogItem = async (catalogId: string, itemId: string): Promise<CatalogItem | null> => {
  try {
    const itemRef = doc(db, 'catalogs', catalogId, 'items', itemId);
    const itemSnap = await getDoc(itemRef);

    if (itemSnap.exists()) {
      return { id: itemSnap.id, ...itemSnap.data() } as CatalogItem;
    }
    return null;
  } catch (error) {
    console.error('Error fetching catalog item:', error);
    throw error;
  }
};

/**
 * Fetch categories for a catalog
 */
export const getCatalogCategories = async (catalogId: string): Promise<Category[]> => {
  try {
    const categoriesRef = collection(db, 'catalogs', catalogId, 'categories');
    const q = query(categoriesRef, orderBy('name', 'asc'));
    const querySnapshot = await getDocs(q);

    const cats = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Category[];
    // Sort by order field (lower numbers first), then name as fallback
    return cats.sort((a, b) => {
      if (a.order !== undefined && b.order !== undefined) return a.order - b.order;
      if (a.order !== undefined) return -1;
      if (b.order !== undefined) return 1;
      return a.name.localeCompare(b.name);
    });
  } catch (error) {
    console.error('Error fetching categories:', error);
    throw error;
  }
};

/**
 * Fetch dietary tags for a catalog
 */
export const getCatalogDietaryTags = async (catalogId: string): Promise<DietaryTag[]> => {
  try {
    const tagsRef = collection(db, 'catalogs', catalogId, 'dietaryTags');
    const q = query(tagsRef, orderBy('name', 'asc'));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as DietaryTag[];
  } catch (error) {
    console.error('Error fetching dietary tags:', error);
    throw error;
  }
};

/**
 * Fetch add-ons for a catalog
 */
export const getCatalogAddOns = async (catalogId: string): Promise<AddOn[]> => {
  try {
    const addOnsRef = collection(db, 'catalogs', catalogId, 'addOns');
    const q = query(addOnsRef, orderBy('name', 'asc'));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as AddOn[];
  } catch (error) {
    console.error('Error fetching add-ons:', error);
    throw error;
  }
};

/**
 * Fetch variations for an item
 * Sorts by order field (if present), then by createdAt as fallback
 */
export const getItemVariations = async (catalogId: string, itemId: string): Promise<ItemVariation[]> => {
  try {
    const variationsRef = collection(db, 'catalogs', catalogId, 'items', itemId, 'variations');
    const querySnapshot = await getDocs(variationsRef);

    const variations = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as ItemVariation[];

    // Sort by order field (lower numbers first), then by createdAt as fallback
    return variations.sort((a, b) => {
      // If both have order, compare by order
      if (a.order !== undefined && b.order !== undefined) {
        return a.order - b.order;
      }
      // Items with order come first
      if (a.order !== undefined) return -1;
      if (b.order !== undefined) return 1;

      // Both have no order, sort by createdAt
      if (a.createdAt && b.createdAt) {
        return a.createdAt.toMillis() - b.createdAt.toMillis();
      }
      return 0;
    });
  } catch (error) {
    console.error('Error fetching variations:', error);
    throw error;
  }
};

/**
 * Search items by name
 */
export const searchCatalogItems = async (catalogId: string, searchTerm: string): Promise<CatalogItem[]> => {
  try {
    const items = await getVisibleCatalogItems(catalogId);
    const lowerSearchTerm = searchTerm.toLowerCase();

    return items.filter(item =>
      item.name.toLowerCase().includes(lowerSearchTerm) ||
      item.description?.toLowerCase().includes(lowerSearchTerm)
    );
  } catch (error) {
    console.error('Error searching items:', error);
    throw error;
  }
};

/**
 * Filter items by category
 */
export const filterItemsByCategory = async (catalogId: string, categoryName: string): Promise<CatalogItem[]> => {
  try {
    const items = await getVisibleCatalogItems(catalogId);
    return items.filter(item => item.category === categoryName);
  } catch (error) {
    console.error('Error filtering items by category:', error);
    throw error;
  }
};

/**
 * Fetch a table by ID
 */
export const getTableById = async (catalogId: string, tableId: string): Promise<Table | null> => {
  try {
    const tableRef = doc(db, 'catalogs', catalogId, 'tables', tableId);
    const tableSnap = await getDoc(tableRef);

    if (tableSnap.exists()) {
      return { id: tableSnap.id, ...tableSnap.data() } as Table;
    }
    return null;
  } catch (error) {
    console.error('Error fetching table:', error);
    throw error;
  }
};
